// Difficulty curve. As the player completes marshmallows the flame gets hotter,
// the golden band narrows, and wind gusts grow stronger. All three are
// monotonic in `marshmallowsDone` and clamped so the game stays playable.

import {
  BASE_FLAME_INTENSITY,
  FLAME_STEP_PER_MARSHMALLOW,
  MAX_FLAME_INTENSITY,
  BASE_BAND_WIDTH,
  BAND_SHRINK_PER_MARSHMALLOW,
  MIN_BAND_WIDTH,
  BAND_CENTER,
  BASE_WIND_AMP,
  WIND_STEP_PER_MARSHMALLOW,
  MAX_WIND_AMP,
} from './constants';

export interface Difficulty {
  /** Flame heat-in rate at full proximity (per second). */
  flameIntensity: number;
  /** Golden band width (heat units), centered on BAND_CENTER. */
  bandWidth: number;
  /** Golden band lower bound. */
  bandMin: number;
  /** Golden band upper bound. */
  bandMax: number;
  /** Wind perturbation amplitude (heat units). */
  windAmp: number;
}

const clamp = (v: number, lo: number, hi: number): number =>
  v < lo ? lo : v > hi ? hi : v;

/** Difficulty parameters for a given number of completed marshmallows (>= 0). */
export function difficultyFor(marshmallowsDone: number): Difficulty {
  const n = Math.max(0, Math.floor(marshmallowsDone));

  const flameIntensity = clamp(
    BASE_FLAME_INTENSITY + FLAME_STEP_PER_MARSHMALLOW * n,
    BASE_FLAME_INTENSITY,
    MAX_FLAME_INTENSITY,
  );

  const bandWidth = clamp(
    BASE_BAND_WIDTH - BAND_SHRINK_PER_MARSHMALLOW * n,
    MIN_BAND_WIDTH,
    BASE_BAND_WIDTH,
  );

  const windAmp = clamp(
    BASE_WIND_AMP + WIND_STEP_PER_MARSHMALLOW * n,
    BASE_WIND_AMP,
    MAX_WIND_AMP,
  );

  return {
    flameIntensity,
    bandWidth,
    bandMin: BAND_CENTER - bandWidth / 2,
    bandMax: BAND_CENTER + bandWidth / 2,
    windAmp,
  };
}
